import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSignOutAllConnectedAppsMutation } from 'src/api/mutations/connectedAppsMutations';
import { useConnectedAppsQuery } from 'src/api/queries/connectedAppsQueries';
import { useAlerts } from 'src/common/AlertManager';
import Button from 'src/components/Button';
import Checkbox from 'src/components/Checkbox';
import Select from 'src/components/Select';
import type { ConnectedAppsScope } from 'src/types/ConnectedApps';
import { config } from 'src/utils/config';
import AvailableAgents from './AvailableAgents';
import ConnectedAppRow from './ConnectedAppRow';
import messages from './messages';
import styles from './ConnectedApps.module.scss';

/** The MCP endpoint people add as a connector. The chat proxy and the MCP
 *  server share a host (Caddy), so it follows REACT_APP_CHAT_PROXY_URL per
 *  environment; production's is the fallback. */
const MCP_URL = (() => {
  try {
    if (config.chatProxyUrl) return `${new URL(config.chatProxyUrl).origin}/mcp`;
  } catch {
    // fall through
  }
  return 'https://mcp.filespin.io/mcp';
})();
const ALL = '__all__';

type Props = {
  scope: ConnectedAppsScope;
  /** Show the explanatory line above the list (the Profile card does). */
  showIntro?: boolean;
};

/**
 * Connected apps (OAuth2 sessions) for the caller, one member, or the whole
 * account. Shared by the Profile card and the two admin modals on the Users
 * page, so the rules — which session runs scheduled agents, what signing out
 * does — are rendered the same way everywhere. filespin-ui-2.0#977.
 */
const ConnectedAppsPanel = ({ scope, showIntro }: Props) => {
  const { formatMessage } = useIntl();
  const { showSuccess, showAlert } = useAlerts();
  const { data, isLoading, error } = useConnectedAppsQuery(scope);
  const apps = useMemo(() => data?.data ?? [], [data]);

  const [userFilter, setUserFilter] = useState(ALL);
  const [appFilter, setAppFilter] = useState(ALL);
  const [confirmAll, setConfirmAll] = useState(false);
  const [stopSchedules, setStopSchedules] = useState(false);

  const isAccount = scope.kind === 'account';
  const shown = useMemo(
    () =>
      apps.filter(
        (app) =>
          (userFilter === ALL || String(app.user_id) === userFilter) &&
          (appFilter === ALL || app.app_name === appFilter),
      ),
    [apps, userFilter, appFilter],
  );

  const userOptions = useMemo(() => {
    const seen = new Map<number, string>();
    apps.forEach((app) => seen.set(app.user_id, app.user_email));
    return [
      { label: formatMessage(messages.allUsers), value: ALL },
      ...Array.from(seen, ([id, email]) => ({ label: email, value: String(id) })).sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    ];
  }, [apps, formatMessage]);

  const appOptions = useMemo(
    () => [
      { label: formatMessage(messages.allApps), value: ALL },
      ...Array.from(new Set(apps.map((app) => app.app_name)))
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ label: name, value: name })),
    ],
    [apps, formatMessage],
  );

  const signOutAll = useSignOutAllConnectedAppsMutation({
    onSuccess: (result) => {
      setConfirmAll(false);
      setStopSchedules(false);
      showSuccess(
        formatMessage(messages.signedOutAll, {
          revoked: result.data.revoked,
          kept: result.data.skipped_schedule_sessions,
        }),
      );
    },
    onError: () => showAlert(formatMessage(messages.actionFailed)),
  });

  const anyBacksSchedules = apps.some((app) => app.backs_schedules);
  // What "Sign out all" will actually sign out: the scheduler's session is
  // kept unless the box is ticked, so say so in the count.
  const signOutAllCount = stopSchedules ? apps.length : apps.filter((app) => !app.backs_schedules).length;
  // Sign out all is per person: the caller's own, or one member's. The
  // account-wide view signs out row by row, so one click can't sign out a
  // whole team.
  const canSignOutAll = !isAccount && apps.length > 0;

  const empty =
    scope.kind === 'self'
      ? formatMessage(messages.emptySelf, { url: <code key="url">{MCP_URL}</code> })
      : formatMessage(isAccount ? messages.emptyAccount : messages.emptyUser);

  const errorStatus = (error as any)?.status;

  return (
    <div className={styles.panel} data-test="connected-apps">
      {(showIntro || canSignOutAll) && (
        <div className={styles.header}>
          <p>{showIntro ? formatMessage(messages.intro) : null}</p>
          {canSignOutAll && !confirmAll && (
            <Button mode="tertiary" size="small" onClick={() => setConfirmAll(true)}>
              {formatMessage(messages.signOutAll)}
            </Button>
          )}
        </div>
      )}

      {confirmAll && (
        <div className={styles.confirmAll} role="alertdialog" aria-label={formatMessage(messages.signOutAll)}>
          <p>{formatMessage(messages.signOutAllConfirm, { count: signOutAllCount })}</p>
          {anyBacksSchedules && (
            <>
              {!stopSchedules && <p>{formatMessage(messages.signOutAllKeepsSchedules)}</p>}
              <Checkbox
                label={formatMessage(messages.alsoStopSchedules)}
                checked={stopSchedules}
                onChange={() => setStopSchedules((value) => !value)}
              />
            </>
          )}
          <div className={styles.confirmButtons}>
            <Button
              mode="decline"
              size="small"
              loading={signOutAll.isLoading}
              onClick={() =>
                signOutAll.mutate({
                  userId: scope.kind === 'user' ? scope.userId : undefined,
                  includeSchedules: stopSchedules,
                })
              }
            >
              {formatMessage(messages.signOutAll)}
            </Button>
            <Button mode="tertiary" size="small" onClick={() => setConfirmAll(false)} disabled={signOutAll.isLoading}>
              {formatMessage(messages.cancel)}
            </Button>
          </div>
        </div>
      )}

      {isAccount && apps.length > 0 && (
        <div className={styles.filters}>
          <Select
            label={formatMessage(messages.filterUser)}
            value={userFilter}
            onChange={setUserFilter}
            options={userOptions}
          />
          <Select
            label={formatMessage(messages.filterApp)}
            value={appFilter}
            onChange={setAppFilter}
            options={appOptions}
          />
        </div>
      )}

      {isLoading ? (
        <p className={styles.status}>{formatMessage(messages.loading)}</p>
      ) : error ? (
        <p className={styles.status} role="alert">
          {formatMessage(errorStatus === 403 ? messages.forbidden : messages.loadError)}
        </p>
      ) : apps.length === 0 ? (
        <p className={styles.empty}>{empty}</p>
      ) : shown.length === 0 ? (
        <p className={styles.empty}>{formatMessage(messages.emptyFiltered)}</p>
      ) : (
        <ul className={styles.list}>
          {shown.map((app) => (
            <ConnectedAppRow key={app.id} app={app} showOwner={isAccount} />
          ))}
        </ul>
      )}

      {scope.kind === 'self' && !isLoading && !error && <AvailableAgents apps={apps} />}
    </div>
  );
};

export default ConnectedAppsPanel;
